import {
    Dropdown,
    Option,
    OptionGroup,
    type OptionOnSelectData,
    type SelectionEvents,
} from '@fluentui/react-components';
import type { Column } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetDateColumnFilterValue } from '../filter/index.js';
import { DATE_FILTER_OPERATION, type DateFilterValuesWithoutInArray } from '../filter/operation.js';
import type { DateFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_DATE_OPTIONS } from './data.js';

type DateOperationProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: DateFilterValuesWithoutInArray['id'];
    resetListFilter: (resetDropdown: boolean) => void;
    setDropdownValue: Dispatch<SetStateAction<DateFilterValuesWithoutInArray['id']>>;
};

export function DateOperation<TData, TValue>({
    column,
    dropdownValue,
    resetListFilter,
    setDropdownValue,
}: DateOperationProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const onDropdownChange = useCallback(
        (_: SelectionEvents, changeData: OptionOnSelectData) => {
            const operation = changeData.selectedOptions[0] as typeof dropdownValue;

            if (
                dropdownValue === (DATE_FILTER_OPERATION.inArray.id as DateFilterValuesWithoutInArray['id']) &&
                operation !== (DATE_FILTER_OPERATION.inArray.id as DateFilterValuesWithoutInArray['id'])
            ) {
                resetListFilter(false);
            }

            setDropdownValue(operation);

            typedSetDateColumnFilterValue(column, {
                ...(column.getFilterValue() as DateFilterSchemaWithoutInArray | undefined),
                operation,
            });
        },
        [column, dropdownValue, resetListFilter, setDropdownValue]
    );

    return (
        <Dropdown
            className={sharedStyles.dropdown}
            value={DATE_FILTER_OPERATION[dropdownValue].label}
            selectedOptions={[dropdownValue]}
            onOptionSelect={onDropdownChange}
            listbox={{
                className: sharedStyles.dropdownListbox,
            }}
        >
            {DROPDOWN_DATE_OPTIONS.map((optionGroup, index) => (
                <OptionGroup key={index}>
                    {optionGroup.map((option) => {
                        return (
                            <Option key={option} value={option} text={DATE_FILTER_OPERATION[option].label}>
                                {DATE_FILTER_OPERATION[option].label}
                            </Option>
                        );
                    })}
                </OptionGroup>
            ))}
        </Dropdown>
    );
}
